import QRCode from 'qrcode';
import { Download, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { ChangeEvent } from "react";
import Toast from '@/components/_shared/Toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { usePageSettings } from '@/lib/cookies';

// Standard-Einstellungen für den QR-Code Generator
const defaultSettings = {
	size: 200,
	darkColor: '#000000',
	lightColor: '#ffffff',
	errorCorrectionLevel: 'M',
	darkMode: true,
};

// Fehlerkorrekturlevel-Optionen
const errorCorrectionLevels = [
	{ value: 'L', label: 'Niedrig (7%)' },
	{ value: 'M', label: 'Mittel (15%)' },
	{ value: 'Q', label: 'Quartil (25%)' },
	{ value: 'H', label: 'Hoch (30%)' },
];

export default function QRCodeForm() {
	const { settings, updateSettings } = usePageSettings(
		'qrcode',
		defaultSettings
	);

	const [text, setText] = useState('');
	const [qrCodeDataURL, setQrCodeDataURL] = useState<string>("");
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const [previewSize, setPreviewSize] = useState(260);

	// QR-Code generieren
	const generateQRCode = useCallback(async () => {
		if (!text.trim()) {
			setQrCodeDataURL("");
			return;
		}

		try {
			const options = {
				errorCorrectionLevel: settings.errorCorrectionLevel as
					| 'L'
					| 'M'
					| 'Q'
					| 'H',
				width: settings.size,
				margin: 1,
				color: {
					dark: settings.darkColor,
					light: settings.lightColor,
				},
			};

			const dataURL = await QRCode.toDataURL(text, options);
			setQrCodeDataURL(dataURL);
		} catch (_err: unknown) {
			// Fehler beim Generieren ignorieren, UI zeigt einfach keinen QR-Code
		}
	}, [
		text,
		settings.size,
		settings.darkColor,
		settings.lightColor,
		settings.errorCorrectionLevel,
	]);

	// Erfolgsmeldung nach einer bestimmten Zeit ausblenden
	useEffect(() => {
		if (successMessage) {
			const timer = setTimeout(() => {
				setSuccessMessage(null);
			}, 3000);
			return () => clearTimeout(timer);
		}
	}, [successMessage]);

	// QR-Code generieren, wenn sich die Eingabe oder Einstellungen ändern
	useEffect(() => {
		void generateQRCode();
	}, [generateQRCode]);

	// QR-Code herunterladen
	const handleDownload = () => {
		if (!qrCodeDataURL) return;

		const link = document.createElement('a');
		link.href = qrCodeDataURL;
		link.download = `qrcode-${Date.now()}.png`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);

		setSuccessMessage('QR-Code wurde heruntergeladen');
	};

	// Änderung der Einstellungen
	const handleSizeChange = (value: number[]) => {
		updateSettings({ size: value[0] });
	};

	const handleColorChange = (e: ChangeEvent<HTMLInputElement>) => {
		updateSettings({ [e.target.name]: e.target.value });
	};

	const handleErrorCorrectionChange = (e: ChangeEvent<HTMLSelectElement>) => {
		updateSettings({
			errorCorrectionLevel: e.target.value as 'L' | 'M' | 'Q' | 'H',
		});
	};

	return (
		<div className="flex flex-col gap-8 md:flex-row">
			{/* Success message toast */}
			<Toast
				message={successMessage || ''}
				onClose={() => setSuccessMessage(null)}
				visible={!!successMessage}
			/>

			{/* QR-Code Einstellungen */}
			<section className="card rounded-xl border border-gray-100 p-6 shadow-sm backdrop-blur-sm md:w-1/2 dark:border-gray-700 dark:bg-gray-800/70">
				<h2 className="mb-4 border-gray-100 border-b pb-2 font-semibold text-gray-800 text-xl dark:border-gray-700 dark:text-gray-100">
					QR-Code erstellen
				</h2>

				<div className="space-y-4">
					<div>
						<Label
							className="mb-2 block font-medium text-gray-600 dark:text-gray-300"
							htmlFor="text-input"
						>
							Text / URL
						</Label>
						<Input
							className="border-gray-200 bg-white transition-all focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:focus:ring-blue-600"
							id="text-input"
							onChange={(e) => setText(e.target.value)}
							placeholder="z.B. https://example.com oder beliebiger Text"
							value={text}
						/>
					</div>

					<div>
						<Label className="mb-2 block font-medium text-gray-600 dark:text-gray-300">
							Größe: {settings.size}px
						</Label>
						<Slider
							className="mb-6"
							max={400}
							min={100}
							onValueChange={handleSizeChange}
							step={10}
							value={[settings.size]}
						/>
					</div>

					<div className="grid grid-cols-2 gap-4">
						<div>
							<Label
								className="mb-2 block font-medium text-gray-600 dark:text-gray-300"
								htmlFor="darkColor"
							>
								Vordergrundfarbe
							</Label>
							<div className="flex items-center">
								<input
									className="h-10 w-full cursor-pointer rounded-md border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
									id="darkColor"
									name="darkColor"
									onChange={handleColorChange}
									type="color"
									value={settings.darkColor}
								/>
							</div>
						</div>

						<div>
							<Label
								className="mb-2 block font-medium text-gray-600 dark:text-gray-300"
								htmlFor="lightColor"
							>
								Hintergrundfarbe
							</Label>
							<div className="flex items-center">
								<input
									className="h-10 w-full cursor-pointer rounded-md border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
									id="lightColor"
									name="lightColor"
									onChange={handleColorChange}
									type="color"
									value={settings.lightColor}
								/>
							</div>
						</div>
					</div>

					<div>
						<Label
							className="mb-2 block font-medium text-gray-600 dark:text-gray-300"
							htmlFor="errorCorrection"
						>
							Fehlerkorrekturlevel
						</Label>
						<select
							className="h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:focus:ring-blue-600"
							id="errorCorrection"
							onChange={handleErrorCorrectionChange}
							value={settings.errorCorrectionLevel}
						>
							{errorCorrectionLevels.map((level) => (
								<option key={level.value} value={level.value}>
									{level.label}
								</option>
							))}
						</select>
					</div>

					<div className="mt-6 flex justify-end">
						<Button
							className="bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
							disabled={!qrCodeDataURL}
							onClick={handleDownload}
							type="button"
						>
							<Download className="mr-2 h-4 w-4" />
							Herunterladen
						</Button>
					</div>
				</div>
			</section>

			{/* QR-Code Vorschau */}
			<section className="card flex flex-col items-center justify-center rounded-xl border border-gray-100 p-6 shadow-sm backdrop-blur-sm md:w-1/2 dark:border-gray-700 dark:bg-gray-800/70">
				<h2 className="mb-4 w-full border-gray-100 border-b pb-2 font-semibold text-gray-800 text-xl dark:border-gray-700 dark:text-gray-100">
					Vorschau
				</h2>
				<div className="flex min-h-[220px] w-full flex-1 items-center justify-center">
					{qrCodeDataURL ? (
						<div className="flex h-full w-full items-center justify-center">
							<img
								alt="QR Code"
								className={
									'rounded-lg border border-gray-200 bg-white object-contain shadow-md dark:border-gray-700'
								}
								src={qrCodeDataURL}
								style={{
									maxWidth: previewSize,
									maxHeight: previewSize,
									width: '100%',
									height: 'auto',
								}}
							/>
						</div>
					) : (
						<div className="w-full p-8 text-center text-gray-500 dark:text-gray-400">
							<RefreshCw className="mx-auto mb-4 h-16 w-16 opacity-20" />
							<p>Gib Text ein, um einen QR-Code zu generieren</p>
						</div>
					)}
				</div>
				{/* Schieberegler für Vorschaugröße */}
				<div className="mt-6 w-full">
					<Label className="mb-2 block font-medium text-gray-600 dark:text-gray-300">
						Vorschaugröße: {previewSize}px
					</Label>
					<Slider
						className="mb-2"
						max={400}
						min={120}
						onValueChange={([v]) => setPreviewSize(v)}
						step={10}
						value={[previewSize]}
					/>
				</div>
			</section>
		</div>
	);
}
